import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import LocalisedPlot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useElementSize } from "hooks/useElementSize";
import { useFormatNumber } from "src/utils/formatNumber";

import { AutoRecipeMonthlyValueMode } from "store/api/generatedApi";
import { monthlyParameterValue } from "./monthlyParameterValues";

const PLOT_MARGIN = { l: 70, r: 25, t: 20, b: 55 };
const DISPLAYED_MONTHS = 12;

export const MonthlyTargetsPlot = ({
  targets,
  defaultTarget,
  onChange,
  disabled,
  label,
  timezone,
  minimum,
  maximum,
  mode,
}: {
  targets: Record<string, number>;
  defaultTarget: unknown;
  onChange: (targets: Record<string, number>) => void;
  disabled: boolean;
  label: string;
  timezone: string;
  minimum: number | undefined;
  maximum: number | undefined;
  mode: AutoRecipeMonthlyValueMode;
}) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber()(2);
  const theme = useTheme();
  const { ref, size } = useElementSize();
  const [firstMonth, setFirstMonth] = useState(() =>
    dayjs().tz(timezone).startOf("month").format("YYYY-MM")
  );
  const [drag, setDrag] = useState<{ month: string; upper: number } | null>(
    null
  );
  const months = Array.from({ length: DISPLAYED_MONTHS }, (_, index) => {
    const month = dayjs(`${firstMonth}-01`).add(index, "month");
    const key = month.format("YYYY-MM-DD");
    const amount = monthlyParameterValue({
      month: key,
      changes: targets,
      startingValue: defaultTarget,
      mode,
    });
    return {
      key,
      label: month.format("MMM YYYY"),
      amount,
    };
  });
  const upper =
    drag?.upper ??
    Math.max(1, ...months.map(({ amount }) => amount ?? 0)) * 1.2;
  const setTarget = (month: string, amount: number) => {
    if (!disabled)
      onChange({
        ...targets,
        [month]: Math.min(maximum ?? Infinity, Math.max(minimum ?? 0, amount)),
      });
  };
  return (
    <Stack gap={1}>
      <TextField
        label={t("autoFirstDisplayedMonth")}
        type="month"
        value={firstMonth}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(event) => {
          if (event.target.value) setFirstMonth(event.target.value);
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {t(
          mode === "carry_forward"
            ? "autoDragMonthlyPrices"
            : "autoDragMonthlyTargets"
        )}
      </Typography>
      {mode === "carry_forward" ? (
        <Typography variant="body2" color="text.secondary">
          {t("autoMonthlyPriceMarkers")}
        </Typography>
      ) : null}
      <Box
        ref={ref}
        sx={{
          position: "relative",
          height: 300,
          "& .js-plotly-plot": { width: "100%" },
        }}
      >
        <LocalisedPlot
          useResizeHandler
          data={[
            {
              type: "scatter",
              mode: "lines",
              x: months.map((_, index) => index),
              y: months.map(({ amount }) => amount),
              line: {
                color: theme.palette.primary.main,
                shape: mode === "carry_forward" ? "hv" : "linear",
              },
              hoverinfo: "skip",
              connectgaps: false,
            },
          ]}
          layout={{
            height: 300,
            autosize: true,
            width: size?.width,
            margin: PLOT_MARGIN,
            xaxis: {
              range: [-0.5, DISPLAYED_MONTHS - 0.5],
              tickmode: "array",
              tickvals: months.map((_, index) => index),
              ticktext: months.map(({ label }) => label),
              fixedrange: true,
              automargin: false,
            },
            yaxis: { range: [0, upper], fixedrange: true, automargin: false },
            dragmode: false,
            showlegend: false,
          }}
          config={{ displayModeBar: false }}
        />
        <Box
          component="svg"
          role="group"
          aria-label={label}
          sx={{
            position: "absolute",
            top: `${PLOT_MARGIN.t}px`,
            left: `${PLOT_MARGIN.l}px`,
            width: `calc(100% - ${PLOT_MARGIN.l + PLOT_MARGIN.r}px)`,
            height: `calc(100% - ${PLOT_MARGIN.t + PLOT_MARGIN.b}px)`,
            overflow: "visible",
            "& circle": {
              cursor: disabled ? "default" : "ns-resize",
              touchAction: "none",
              fill: theme.palette.primary.main,
              stroke: theme.palette.primary.main,
              strokeWidth: 2,
              "&[data-inherited=true]": {
                fill: theme.palette.background.paper,
              },
              "&:focus": {
                outline: "none",
                stroke: theme.palette.text.primary,
                strokeWidth: 3,
              },
            },
          }}
        >
          {months.map(({ key, label: monthLabel, amount }, index) =>
            amount === null ? null : (
              <circle
                key={key}
                data-inherited={mode === "carry_forward" && !(key in targets)}
                cx={`${((index + 0.5) / DISPLAYED_MONTHS) * 100}%`}
                cy={`${(1 - amount / upper) * 100}%`}
                r={7}
                role="slider"
                aria-label={`${monthLabel}: ${label}`}
                aria-orientation="vertical"
                aria-valuemin={minimum ?? 0}
                aria-valuemax={maximum ?? upper}
                aria-valuenow={amount}
                aria-valuetext={formatNumber(amount)}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (
                    [
                      "ArrowUp",
                      "ArrowRight",
                      "ArrowDown",
                      "ArrowLeft",
                    ].includes(event.key)
                  ) {
                    event.preventDefault();
                    const direction =
                      event.key === "ArrowUp" || event.key === "ArrowRight"
                        ? 1
                        : -1;
                    setTarget(
                      key,
                      amount + direction * (event.shiftKey ? 100 : 1)
                    );
                  }
                }}
                onPointerDown={(event) => {
                  if (disabled || event.button !== 0) return;
                  event.preventDefault();
                  event.currentTarget.focus();
                  event.currentTarget.setPointerCapture(event.pointerId);
                  setDrag({ month: key, upper });
                }}
                onPointerMove={(event) => {
                  if (disabled || drag?.month !== key) return;
                  const bounds =
                    event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                  if (!bounds || bounds.height === 0) return;
                  const fraction = Math.min(
                    1,
                    Math.max(0, (bounds.bottom - event.clientY) / bounds.height)
                  );
                  setTarget(key, Math.round(fraction * drag.upper));
                }}
                onPointerUp={(event) => {
                  if (event.currentTarget.hasPointerCapture(event.pointerId))
                    event.currentTarget.releasePointerCapture(event.pointerId);
                  setDrag(null);
                }}
                onLostPointerCapture={() => {
                  setDrag(null);
                }}
                onPointerCancel={() => {
                  setDrag(null);
                }}
              >
                <title>
                  {t("autoTargetMonthValue", {
                    month: monthLabel,
                    amount: formatNumber(amount),
                  })}
                </title>
              </circle>
            )
          )}
        </Box>
      </Box>
    </Stack>
  );
};
